import { lazy, type ComponentType } from 'react';
import { isMissingChunk } from '@/core/staleBuild';

/* =============================================================================
 * A page whose code went missing between deploys
 * -----------------------------------------------------------------------------
 * Every route below is code-split, so opening one fetches a file named after a
 * hash of its contents — EmployeesPage-ChFhk3kQ.js. The hash changes whenever
 * the code does, which is what lets those files be cached forever.
 *
 * It also means a deploy renames them. A tab that loaded the app twenty minutes
 * ago is holding an index.html that lists the *old* names, and those files are
 * no longer on the server. Clicking Employees asks for a file that returns 404,
 * the dynamic import rejects, and the error boundary catches:
 *
 *     Failed to fetch dynamically imported module: .../EmployeesPage-ChFhk3kQ.js
 *
 * Reloading fixes it because the reload fetches the new index.html with the new
 * names — which is exactly why it looks intermittent and unreproducible, and why
 * it lands on whoever had the app open while a deploy went out.
 *
 * -----------------------------------------------------------------------------
 * Why a reload rather than a retry
 * -----------------------------------------------------------------------------
 * Retrying the same URL cannot help: the file is not missing because the network
 * flickered, it is missing because it no longer exists. The only thing that can
 * recover is fetching the document again to learn the new names, so that is what
 * this does.
 *
 * -----------------------------------------------------------------------------
 * Why it is allowed to do that only once
 * -----------------------------------------------------------------------------
 * If a chunk is genuinely unreachable — the deploy is broken, the CDN is serving
 * a half-published build, the user is behind a proxy mangling the response — a
 * reload will fail the same way, reload again, and the app becomes a loop the
 * user cannot escape or report. That is a far worse failure than the error
 * message, so the attempt is recorded in sessionStorage and the second one is
 * allowed to fail loudly.
 *
 * sessionStorage rather than a module variable: the reload wipes the module.
 * Scoped per tab, and cleared when the tab closes.
 * ========================================================================== */

const RELOAD_KEY = 'noorone.chunkReloadAt';

/** How long a reload counts as "just tried". */
const RECENT_MS = 20_000;

function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    return at > 0 && Date.now() - at < RECENT_MS;
  } catch {
    // Storage unavailable — private mode, or a locked-down webview. Better to
    // never auto-reload than to risk looping with no way to record the attempt.
    return true;
  }
}

function rememberReload(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* handled by reloadedRecently returning true */
  }
}

/**
 * Whether this error is worth reloading the page for.
 *
 * Exported so the decision can be tested on its own. Everything around it —
 * lazy(), the reload, the promise that never settles — needs a browser and a
 * React tree to exercise, and none of it is where the mistakes live.
 */
export function shouldRecover(error: unknown): boolean {
  return isMissingChunk(error) && !reloadedRecently();
}

/**
 * A lazily loaded route that survives a deploy landing under somebody's feet.
 *
 * Used exactly like `lazy(() => import(...))`, with the named export picked out
 * by the second argument.
 */
export function lazyRoute<M, K extends keyof M>(
  load: () => Promise<M>,
  pick: K,
) {
  return lazy(async () => {
    try {
      const mod = await load();
      return { default: mod[pick] as ComponentType<Record<string, never>> };
    } catch (error) {
      if (!shouldRecover(error)) throw error;

      rememberReload();
      window.location.reload();

      /* Never resolves, on purpose. The reload is already under way; resolving
         or rejecting here would render something — a flash of the error card, or
         a half-mounted page — over a document that is being replaced. */
      return new Promise<never>(() => {});
    }
  });
}

/* Re-exported where it has always been imported from. The definition moved to
   core so errorMessage can reach it too; the call sites did not. */
export { isMissingChunk };
